/*
 * @providesModule BadgeController
 *
 */

import _ from 'lodash';
import ChatManager from 'ChatManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import DateUtils from 'DateUtils';
import GlobalEventEmitter from 'GlobalEventEmitter';
import HSConstants from 'HomeScreenConstants';
import NetClient from 'NetClient';
import Preferences from 'Preferences';
import RewardCalendarController from 'RewardCalendarController';
import {currentRewardIsClaimed} from 'RewardCalendarUtils';
import SKZConstants from 'SKZConstants';
import SKZPersonalInfo from 'SKZPersonalInfo';
import SKZPlayerProfile from 'SKZPlayerProfile';
import {SKZUserInterface} from 'SKZUser';

import {NativeModules, Platform} from 'react-native';

const SKZAPIManager = NativeModules.SKZAPIManager;
const HOME_TAB = HSConstants.STREAM;
const PLAY_TAB = HSConstants.PLAY;
const STORE_TAB = HSConstants.STORE;
const MENU_TAB = HSConstants.MENU;
const CHAT_BUTTON = SKZConstants.SKZ_CHAT_BUTTON;
const SKILLZ_NEWS_CHANNEL = SKZConstants.DEFAULT_SKILLZ_NEWS_CHANNEL_NAME;
const UNCLAIMED_TROPHIES_COUNT = 'trophiesAwardedAmountSKZ';
const LTO_URL = 'store/limited_time_offers';
const SAVE_ACCOUNT_BUTTON = SKZConstants.SAVE_ACCOUNT_BUTTON;
const PHONE_VERIFY_BUTTON = SKZConstants.PHONE_VERIFY_BUTTON;
const isWeb = Platform.OS === 'web';
const SDK_DEPOSIT_COOKIE_NAME = '_sklz_sdk_deposit';
type LTO = {open_in_store: boolean; end: string; id: string; type: string};
type Error = {responseObject: Array<{message: string; name: string}>};

class BadgeController {
  homeTabBadgeCount: number;
  playTabBadgeCount: number;
  storeTabBadgeCount: number;
  menuTabBadgeCount: number;
  chatBadgeCount: number;
  skillzNewsChannelBadgeCount: number;
  activeLTO: LTO | undefined;
  unclaimedTrophies: number;
  activeLTOBadgeAvailable: boolean;
  saveAccountBadgeCount: number;
  shouldUpdateSkillzNewsBadgeCount: boolean;
  shouldUpdateSkillzNewsBadgeCountOnTab: boolean;
  loginBonusBadgeCount: number;
  activeLTOBadgeCount: number;
  phoneVerifyBadgeCount: number;
  rewardCalendarBadgeCount: number;

  constructor() {
    this.homeTabBadgeCount = 0;
    this.playTabBadgeCount = 0;
    this.storeTabBadgeCount = Preferences.get(UNCLAIMED_TROPHIES_COUNT);
    this.menuTabBadgeCount = 0;
    this.chatBadgeCount = 0;
    this.skillzNewsChannelBadgeCount = 0;
    this.activeLTO = Preferences.get(SKZConstants.SKZ_ACTIVE_LTO)
      ? JSON.parse(Preferences.get(SKZConstants.SKZ_ACTIVE_LTO))
      : null;
    this.shouldUpdateSkillzNewsBadgeCount = true;
    this.shouldUpdateSkillzNewsBadgeCountOnTab = true;
    this.unclaimedTrophies = Preferences.get(UNCLAIMED_TROPHIES_COUNT);
    this.loginBonusBadgeCount = 0;
    this.activeLTOBadgeCount = 0;
    this.activeLTOBadgeAvailable = false;
    this.saveAccountBadgeCount = 0;
    this.phoneVerifyBadgeCount = 0;
    this.rewardCalendarBadgeCount = 0;
    if (!isWeb) {
      this.checkAccountMenuBadges(true, true);
    }
    const isSDKDeposit =
      isWeb && typeof document !== 'undefined' && document.cookie.includes(`${SDK_DEPOSIT_COOKIE_NAME}=1`);
    if (
      !isSDKDeposit &&
      (!isWeb || (isWeb && !ClientPropertyConstants.getInterstitialsEnabled()))
    ) {
      this.updateLTO();
    }
  }

  shouldShowBadge(location: string): boolean {
    if (location == HOME_TAB) {
      return this.homeTabBadgeCount > 0;
    } else if (location == PLAY_TAB) {
      return this.playTabBadgeCount > 0;
    } else if (location == STORE_TAB) {
      return this.storeTabBadgeCount > 0;
    } else if (location == MENU_TAB) {
      return this.menuTabBadgeCount > 0;
    } else if (location == SKILLZ_NEWS_CHANNEL) {
      return this.skillzNewsChannelBadgeCount > 0;
    } else if (location == CHAT_BUTTON) {
      return this.chatBadgeCount > 0;
    } else {
      console.warn('BadgeController: No location was passed in');
      return false;
    }
  }

  shouldShowTrophiesBadge(): boolean {
    return this.unclaimedTrophies > 0;
  }

  getBadgeCount(location: string): number {
    if (location == HOME_TAB) {
      return this.homeTabBadgeCount;
    } else if (location == PLAY_TAB) {
      return this.playTabBadgeCount;
    } else if (location == STORE_TAB) {
      return this.storeTabBadgeCount;
    } else if (location == MENU_TAB) {
      return this.menuTabBadgeCount;
    } else if (location == SKILLZ_NEWS_CHANNEL) {
      return this.skillzNewsChannelBadgeCount;
    } else if (location == SAVE_ACCOUNT_BUTTON) {
      return this.saveAccountBadgeCount;
    } else if (location == PHONE_VERIFY_BUTTON) {
      return this.phoneVerifyBadgeCount;
    } else if (location == CHAT_BUTTON) {
      return this.chatBadgeCount;
    } else {
      console.warn('BadgeController: No location was passed in');
      return 0;
    }
  }

  addBadgeCount(location: string) {
    if (location == HOME_TAB) {
      this.homeTabBadgeCount++;
      GlobalEventEmitter.emit('HomeBadgeCountChanged', this.homeTabBadgeCount);
    } else if (location == PLAY_TAB) {
      this.playTabBadgeCount++;
      GlobalEventEmitter.emit('PlayBadgeCountChanged', this.playTabBadgeCount);
    } else if (location == STORE_TAB) {
      this.storeTabBadgeCount++;
      GlobalEventEmitter.emit('StoreBadgeCountChanged', this.storeTabBadgeCount);
    } else if (location == SKILLZ_NEWS_CHANNEL) {
      this.skillzNewsChannelBadgeCount++;
      GlobalEventEmitter.emit(
        'SkillzNewsChannelBadgeCountChanged',
        this.skillzNewsChannelBadgeCount
      );
    }
  }

  subtractBadgeCount(location: string) {
    if (location == HOME_TAB) {
      this.homeTabBadgeCount = Math.max(0, this.homeTabBadgeCount - 1);
      GlobalEventEmitter.emit('HomeBadgeCountChanged', this.homeTabBadgeCount);
    } else if (location == PLAY_TAB) {
      this.playTabBadgeCount = Math.max(0, this.playTabBadgeCount - 1);
      GlobalEventEmitter.emit('PlayBadgeCountChanged', this.playTabBadgeCount);
    } else if (location == STORE_TAB) {
      this.storeTabBadgeCount = Math.max(0, this.storeTabBadgeCount - 1);
      GlobalEventEmitter.emit('StoreBadgeCountChanged', this.storeTabBadgeCount);
    } else if (location == SKILLZ_NEWS_CHANNEL) {
      this.skillzNewsChannelBadgeCount = Math.max(0, this.skillzNewsChannelBadgeCount - 1);
      GlobalEventEmitter.emit(
        'SkillzNewsChannelBadgeCountChanged',
        this.skillzNewsChannelBadgeCount
      );
    }
  }

  clearBadges(location: string) {
    if (location == HOME_TAB) {
      this.homeTabBadgeCount = 0;
      GlobalEventEmitter.emit('HomeBadgeCountChanged', this.homeTabBadgeCount);
    } else if (location == PLAY_TAB) {
      this.playTabBadgeCount = 0;
      GlobalEventEmitter.emit('PlayBadgeCountChanged', this.playTabBadgeCount);
    } else if (location == STORE_TAB) {
      if (this.activeLTOBadgeAvailable) {
        if (this.loginBonusBadgeCount == 0 && this.unclaimedTrophies == 0) {
          this.storeTabBadgeCount = 0;
          GlobalEventEmitter.emit('StoreBadgeCountChanged', this.storeTabBadgeCount);
        } else {
          this.subtractBadgeCount(location);
        }
        this._updateLTOBadgesCleared();
        this.activeLTOBadgeAvailable = false;
      }
    } else if (location == SKILLZ_NEWS_CHANNEL) {
      if (ClientPropertyConstants.getSkillzNewsBannerLocation() == HOME_TAB) {
        this.homeTabBadgeCount = Math.max(
          0,
          this.homeTabBadgeCount - this.skillzNewsChannelBadgeCount
        );
        GlobalEventEmitter.emit('HomeBadgeCountChanged', this.homeTabBadgeCount);
      } else {
        this.playTabBadgeCount = Math.max(
          0,
          this.playTabBadgeCount - this.skillzNewsChannelBadgeCount
        );
        GlobalEventEmitter.emit('PlayBadgeCountChanged', this.playTabBadgeCount);
      }
      this.skillzNewsChannelBadgeCount = 0;
      GlobalEventEmitter.emit(
        'SkillzNewsChannelBadgeCountChanged',
        this.skillzNewsChannelBadgeCount
      );
    } else if (location == SAVE_ACCOUNT_BUTTON) {
      this.saveAccountBadgeCount = 0;
      GlobalEventEmitter.emit('SaveAccountBadgeCountChanged');
      GlobalEventEmitter.emit('MenuBadgeCountChanged');
    } else if (location == PHONE_VERIFY_BUTTON) {
      this.phoneVerifyBadgeCount = 0;
      GlobalEventEmitter.emit('PhoneVerifyBadgeCountChanged');
      GlobalEventEmitter.emit('MenuBadgeCountChanged');
    }
  }

  clearStoredPrefs() {
    Preferences.set({[SKZConstants.SKZ_LTO_BADGE_SHOWN_IDS]: null});
    Preferences.set({[SKZConstants.SKZ_LTO_SHOW_BADGE]: false});
    Preferences.set({[SKZConstants.SKZ_ACTIVE_LTO]: null});
    Preferences.set({[SKZConstants.SAVE_ACCOUNT_BADGE_LAST_SEEN]: null});
    Preferences.set({[SKZConstants.PHONE_VERIFY_BADGE_LAST_SEEN]: null});
  }

  _updateLTOBadgesCleared() {
    const ids = Preferences.get(SKZConstants.SKZ_LTO_BADGE_SHOWN_IDS)
      ? JSON.parse(Preferences.get(SKZConstants.SKZ_LTO_BADGE_SHOWN_IDS))
      : [];
    if (this.activeLTO && this.activeLTO.id) {
      ids.push(this.activeLTO.id);
    }

    Preferences.set({[SKZConstants.SKZ_LTO_BADGE_SHOWN_IDS]: JSON.stringify(ids)});
    Preferences.set({[SKZConstants.SKZ_LTO_SHOW_BADGE]: false});
    Preferences.set({[SKZConstants.SKZ_ACTIVE_LTO]: null});
  }

  _didShowLTOBadgeForId(idCheck: string): boolean {
    const ids = Preferences.get(SKZConstants.SKZ_LTO_BADGE_SHOWN_IDS)
      ? JSON.parse(Preferences.get(SKZConstants.SKZ_LTO_BADGE_SHOWN_IDS))
      : [];

    return _.includes(ids, idCheck);
  }

  _shouldShowActiveLTOBadge(lto: LTO): boolean {
    return lto && !lto.end && !lto.open_in_store && !this._didShowLTOBadgeForId(lto.id);
  }

  shouldOpenToStore(): boolean {
    return !!(this.activeLTO && this.activeLTO.open_in_store);
  }

  getStoreTabBadgeTotal() {
    CurrentUserStore.fetchUserProfile(true)
      .then(async (profile: SKZPlayerProfile) => {
        if (profile) {
          this.loginBonusBadgeCount =
            profile.login_bonus.login_bonus_available &&
            ClientPropertyConstants.getLoginBonusEnabled()
              ? 1
              : 0;
          this.unclaimedTrophies =
            ClientPropertyConstants.getStoreTrophiesButtonEnabled() && Platform.OS !== 'web'
              ? Preferences.get(UNCLAIMED_TROPHIES_COUNT)
              : 0;
          this.activeLTOBadgeCount = this.activeLTOBadgeAvailable ? 1 : 0;
          this.rewardCalendarBadgeCount = (await this.checkRewardCalendarReady()) ? 1 : 0;
          this.storeTabBadgeCount =
            this.unclaimedTrophies +
            this.activeLTOBadgeCount +
            this.loginBonusBadgeCount +
            this.rewardCalendarBadgeCount;
          console.debug(
            `BadgeController: calculating store badge total. Total: ${this.storeTabBadgeCount}, trophies: ${this.unclaimedTrophies}, lto: ${this.activeLTOBadgeCount}, login bonus: ${this.loginBonusBadgeCount}, reward calendar: ${this.rewardCalendarBadgeCount}`
          );
          GlobalEventEmitter.emit('StoreBadgeCountChanged', this.storeTabBadgeCount);
          GlobalEventEmitter.emit('MenuBadgeCountChanged');
        }
      })
      .catch((error: Error) => {
        console.debug('BadgeController: get user personal info error ' + JSON.stringify(error));
      });
  }

  updateLTO() {
    NetClient.getURL(LTO_URL, null, false, SKZAPIManager.OnError.failSilently)
      .then((payload: Array<LTO>) => {
        this.activeLTO = _.find(payload, {type: 'FIRST_TIME_USER'});
        if (!this.activeLTO) {
          this.activeLTO = _.find(payload, {type: 'ALL'});
        }

        if (this.activeLTO) {
          if (this._shouldShowActiveLTOBadge(this.activeLTO) && !this.activeLTOBadgeAvailable) {
            this.activeLTOBadgeAvailable = true;
            this.addBadgeCount(STORE_TAB);
          }
          Preferences.set({[SKZConstants.SKZ_ACTIVE_LTO]: JSON.stringify(this.activeLTO)});
          Preferences.set({
            [SKZConstants.SKZ_LTO_SHOW_BADGE]: this._shouldShowActiveLTOBadge(this.activeLTO),
          });
        } else {
          this.clearBadges(STORE_TAB);
        }
      })
      .catch((error: Error) => {
        console.debug('BadgeController: error fetching LTO badge: ' + JSON.stringify(error));
      });
  }

  getMenuTabBadgeCount(): number {
    this.menuTabBadgeCount =
      parseInt(Preferences.get('unreadSupportMessagesCountSKZ')) +
      this.unclaimedTrophies +
      this.saveAccountBadgeCount +
      this.phoneVerifyBadgeCount;
    // only apply for users who have chat enabled
    if (ClientPropertyConstants.getIsPlayerChatEnabled() && !ChatManager.isUserChatBanned()) {
      this.menuTabBadgeCount += ChatManager.getChatNotificationCount();
    }

    return this.menuTabBadgeCount;
  }

  getChatHeaderButtonBadgeCount(): number {
    if (ClientPropertyConstants.getIsPlayerChatEnabled() && !ChatManager.isUserChatBanned()) {
      this.chatBadgeCount = ChatManager.getChatNotificationCount();
    }

    return this.chatBadgeCount;
  }

  setSkillzNewsBadgeCount(count: number) {
    if (this.shouldUpdateSkillzNewsBadgeCount) {
      this.skillzNewsChannelBadgeCount = count;
      GlobalEventEmitter.emit(
        'SkillzNewsChannelBadgeCountChanged',
        this.skillzNewsChannelBadgeCount
      );
      GlobalEventEmitter.emit('MenuBadgeCountChanged');
      this.shouldUpdateSkillzNewsBadgeCount = false;
    }
  }

  setSkillzNewsBadgeCountOnTab(count: number, location: string) {
    if (this.shouldUpdateSkillzNewsBadgeCountOnTab) {
      if (location == HOME_TAB) {
        this.homeTabBadgeCount += count;
        GlobalEventEmitter.emit('HomeBadgeCountChanged', this.homeTabBadgeCount);
      } else if (location == PLAY_TAB) {
        this.playTabBadgeCount += count;
        GlobalEventEmitter.emit('PlayBadgeCountChanged', this.playTabBadgeCount);
      }

      this.shouldUpdateSkillzNewsBadgeCountOnTab = false;
    }
  }

  checkAccountMenuBadges(useCacheUserInfo: boolean, useCacheProfile: boolean) {
    CurrentUserStore.fetchUserProfile(useCacheProfile)
      .then((profile: SKZPlayerProfile) => {
        this._updateSaveAccountBadgeCount(useCacheUserInfo, profile);
        this._updatePhoneVerifyBadgeCount(useCacheUserInfo, profile);
      })
      .catch((error: Error) => {
        console.debug('BadgeController: get user profile error ' + JSON.stringify(error));
      });
  }

  shouldShowRewardCalendarBadge(): boolean {
    return this.rewardCalendarBadgeCount > 0;
  }

  async checkRewardCalendarReady(): Promise<boolean> {
    if (!ClientPropertyConstants.getRewardCalendarEnabled()) {
      return false;
    }

    const calendar = await RewardCalendarController.getRewardCalendarData(false).catch(
      (error: Error) => {
        console.warn(
          'BadgeController: failed to fetch daily reward calendar to check timestamp - ' +
            JSON.stringify(error)
        );
      }
    );

    if (!calendar) {
      return false;
    }

    return !currentRewardIsClaimed(calendar);
  }

  _updateSaveAccountBadgeCount(useCacheUserInfo: boolean, profile: SKZPlayerProfile) {
    if (profile?.total_games_played < ClientPropertyConstants.getSaveAccountBadgeGamesRequired()) {
      this.saveAccountBadgeCount = 0;
      return;
    }
    CurrentUserStore.getUserData(useCacheUserInfo)
      .then((userInfo: SKZUserInterface) => {
        if (!userInfo || CurrentUserStore.isSavedAccount()) {
          this.saveAccountBadgeCount = 0;
        } else {
          const lastSeen = Preferences.get(SKZConstants.SAVE_ACCOUNT_BADGE_LAST_SEEN);
          if (!lastSeen) {
            this.saveAccountBadgeCount = 1;
            GlobalEventEmitter.emit('SaveAccountBadgeCountChanged');
            GlobalEventEmitter.emit('MenuBadgeCountChanged');
            return;
          }
          const now = Date.now();
          const diff = DateUtils.minutesBetween(now, lastSeen);
          if (diff >= ClientPropertyConstants.getSaveAccountBadgeMinutes()) {
            this.saveAccountBadgeCount = 1;
            GlobalEventEmitter.emit('SaveAccountBadgeCountChanged');
            GlobalEventEmitter.emit('MenuBadgeCountChanged');
          } else {
            this.saveAccountBadgeCount = 0;
          }
        }
      })
      .catch((error: Error) => {
        console.debug('BadgeController: get user personal info error ' + JSON.stringify(error));
      });
  }

  setSaveAccountBadgeCountAsSeen() {
    this.saveAccountBadgeCount = 0;
    GlobalEventEmitter.emit('SaveAccountBadgeCountChanged');
    GlobalEventEmitter.emit('MenuBadgeCountChanged');
    const now = Date.now();
    Preferences.set({[SKZConstants.SAVE_ACCOUNT_BADGE_LAST_SEEN]: now});
  }

  _updatePhoneVerifyBadgeCount(useCachePersonalInfo: boolean, profile: SKZPlayerProfile) {
    if (profile?.total_games_played < ClientPropertyConstants.getVerifyPhoneBadgeGamesRequired()) {
      this.phoneVerifyBadgeCount = 0;
      return;
    }
    CurrentUserStore.fetchUserPersonalInfo(useCachePersonalInfo)
      .then((personalInfo: SKZPersonalInfo) => {
        if (!personalInfo || personalInfo.phone_verified || !personalInfo.email) {
          this.phoneVerifyBadgeCount = 0;
        } else {
          const lastSeen = Preferences.get(SKZConstants.PHONE_VERIFY_BADGE_LAST_SEEN);
          if (!lastSeen) {
            this.phoneVerifyBadgeCount = 1;
            GlobalEventEmitter.emit('PhoneVerifyBadgeCountChanged');
            GlobalEventEmitter.emit('MenuBadgeCountChanged');
            return;
          }
          const now = Date.now();
          const diff = DateUtils.minutesBetween(now, lastSeen);
          if (diff >= ClientPropertyConstants.getVerifyPhoneBadgeMinutes()) {
            this.phoneVerifyBadgeCount = 1;
            GlobalEventEmitter.emit('PhoneVerifyBadgeCountChanged');
            GlobalEventEmitter.emit('MenuBadgeCountChanged');
          } else {
            this.phoneVerifyBadgeCount = 0;
          }
        }
      })
      .catch((error: Error) => {
        console.debug('BadgeController: get user personal info error ' + JSON.stringify(error));
      });
  }

  setPhoneVerifyBadgeCountAsSeen() {
    this.phoneVerifyBadgeCount = 0;
    GlobalEventEmitter.emit('PhoneVerifyBadgeCountChanged');
    GlobalEventEmitter.emit('MenuBadgeCountChanged');
    const now = Date.now();
    Preferences.set({[SKZConstants.PHONE_VERIFY_BADGE_LAST_SEEN]: now});
  }
}

export default new BadgeController();